import { clauseInterval, contains, type Interval, overlaps, valueInterval } from './interval';
import type { AwpValue, LabStatus, ReferenceBand } from './types';

type BandTier = 'optimal' | 'normal' | 'moderate' | 'high';

/** The reference table's tier labels. It writes them by hand, so they're matched trimmed and case-insensitively. */
const TIER_BY_LABEL: ReadonlyMap<string, BandTier> = new Map([
  ['optimal', 'optimal'],
  ['normal', 'normal'],
  ['moderate', 'moderate'],
  ['high', 'high'],
]);

const tierOf = (label: string): BandTier | undefined => TIER_BY_LABEL.get(label.trim().toLowerCase());

/** Whether a band label is one of the reference table's four tiers. `labStatus` ignores a band whose label isn't. */
export const isKnownBandLabel = (label: string): boolean => tierOf(label) !== undefined;

/** Worst first: a value two bands claim takes the more serious tier. */
const SEVERITY: readonly BandTier[] = ['high', 'moderate', 'optimal', 'normal'];

const FLAGGED: ReadonlySet<LabStatus> = new Set(['moderate', 'high', 'outOfRange']);
const OPTIMAL: ReadonlySet<LabStatus> = new Set(['optimal', 'normal']);

/** A status the dashboard counts as needing attention. */
export const isFlagged = (status: LabStatus): boolean => FLAGGED.has(status);

/** A status the dashboard counts as in range. `unknown` is neither flagged nor optimal. */
export const isOptimal = (status: LabStatus): boolean => OPTIMAL.has(status);

interface TieredBand {
  tier: BandTier;
  intervals: Interval[];
}

/** The bands with a recognised tier; any other band is ignored. `toAwpPanel` reports unrecognised labels. */
function tieredBands(bands: ReferenceBand[]): TieredBand[] {
  return bands.flatMap((band) => {
    const tier = tierOf(band.label);
    if (!tier) return [];
    const intervals = band.clauses.map(clauseInterval).filter((interval): interval is Interval => interval !== null);
    return [{ tier, intervals }];
  });
}

/**
 * A lab's status, judged only against the reference table's bands (Quest's H/L flag is never read). Pure: no
 * telemetry, so it's safe to recompute on any render.
 *
 * The band wholly containing the value gives its tier; when two do, the worse one wins. A value no band contains
 * is `outOfRange` if it lies wholly outside a biomarker the table defines by Normal alone, and `unknown`
 * otherwise — a gap between tiers, a bound straddling an edge, no bands at all, or a result that isn't a number.
 */
export function labStatus(value: AwpValue, bands: ReferenceBand[] | null): LabStatus {
  const tiered = tieredBands(bands ?? []);
  const interval = valueInterval(value);
  if (!interval || tiered.length === 0) return 'unknown';

  const matched = new Set(
    tiered.filter((band) => band.intervals.some((clause) => contains(clause, interval))).map((band) => band.tier),
  );
  const worst = SEVERITY.find((tier) => matched.has(tier));
  if (worst) return worst;

  // Only a Normal-only biomarker can be out of range: where other tiers exist, missing them all is a gap.
  if (!tiered.every((band) => band.tier === 'normal')) return 'unknown';
  const normal = tiered.flatMap((band) => band.intervals);
  const outsideNormal = normal.length > 0 && normal.every((range) => !overlaps(range, interval));
  return outsideNormal ? 'outOfRange' : 'unknown';
}
